import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { X, ArrowRight, RefreshCw, Home, AlertCircle, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useApiQuery } from '../../hooks/useApiQuery';

interface PaymentStatusResponse {
  success: boolean;
  data: {
    reference: string;
    status: string;
    amount: number;
  currency: string;
    planName: string;
    paymentDate: string;
    nextBillingDate?: string;
    customerEmail?: string;
    paymentMethod?: string;
    failureReason?: string;
  };
}

interface LocationState {
  plan?: {
    name: string;
    price: string;
    period: string;
    description: string;
    amount: string;
  };
  error?: string;
  paymentId?: string;
  timestamp?: string;
}

const PartnerUpgradeFailure: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState;
  
  // Get reference from URL parameters (from Stripe redirect)
  const reference = searchParams.get('reference');
  
  // Fetch payment status using the reference
  const { data: paymentStatus, isLoading, error } = useApiQuery<PaymentStatusResponse>({
    queryKey: ['payment-status', reference],
    request: {
      url: `/partner/payments/status?reference=${reference}`,
      method: 'GET',
    },
    enabled: !!reference, // Only fetch if reference exists
  });

  // Show loading state while fetching payment status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  const handleRetryPayment = () => {
    navigate('/partner/upgrade');
  };

  const handleGoToDashboard = () => {
    navigate('/partner/dashboard');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:info@123-vote.com?subject=Payment Failure - Partner Upgrade';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 py-10 px-4 sm:px-6 lg:px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
          >
            <X className="w-10 h-10 text-red-600" />
          </motion.div>

          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Payment Failed
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            We couldn't process your payment at this time
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Details</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Attempted Plan</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {paymentStatus?.data?.planName || state?.plan?.name || 'Selected Plan'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {paymentStatus?.data ? 
                      `${paymentStatus.data.currency} ${paymentStatus.data.amount}` :
                      `${state?.plan?.amount || '0'}${state?.plan?.period || ''}`
                    }
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Error Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2" />
                      {paymentStatus?.data?.status || 'Failed'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Reference:</span>
                    <span className="font-mono text-gray-900">
                      {paymentStatus?.data?.reference || state?.paymentId || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="text-gray-900">
                      {paymentStatus?.data?.paymentDate ? 
                        format(new Date(paymentStatus.data.paymentDate), 'MMMM d, yyyy h:mm a') :
                        state?.timestamp ? 
                          format(new Date(state.timestamp), 'MMMM d, yyyy h:mm a') :
                          new Date().toLocaleString()
                      }
                    </span>
                  </div>
                  {paymentStatus?.data?.failureReason && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reason:</span>
                      <span className="text-red-600">{paymentStatus.data.failureReason}</span>
                    </div>
                  )}
                  {paymentStatus?.data?.paymentMethod && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="text-gray-900">{paymentStatus.data.paymentMethod}</span>
                    </div>
                  )}
                  {paymentStatus?.data?.customerEmail && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="text-gray-900">{paymentStatus.data.customerEmail}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Common Issues</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Insufficient funds or daily limit reached</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Card details expired or incorrect</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Bank declined the transaction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Network connectivity issues</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions & Support */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What You Can Do</h2>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">Try Again</h3>
                  <p className="text-blue-800 text-sm mb-3">
                    Most payment issues are temporary. You can retry your payment with the same card or try a different payment method.
                  </p>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• Check your card details and balance</li>
                    <li>• Ensure your billing information is correct</li>
                    <li>• Try a different payment method if available</li>
                  </ul>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Your account has not been charged. No payment was processed due to the failure.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Next Steps</h2>

              <div className="space-y-3">
                <motion.button
                  onClick={handleRetryPayment}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RefreshCw className="w-5 h-5" />
                  Retry Payment
                  <ArrowRight className="w-4 h-4" />
                </motion.button>

                <motion.button
                  onClick={handleContactSupport}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-full font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <CreditCard className="w-5 h-5" />
                  Contact Support
                </motion.button>

                <motion.button
                  onClick={handleGoToDashboard}
                  className="w-full border border-gray-300 text-gray-600 py-3 px-6 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Home className="w-5 h-5" />
                  Go to Dashboard
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Support Information */}
        <motion.div
          className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Support Options</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• Email us at support@123vote.com</p>
                <p>• Call our support team: 1-800-VOTE-123</p>
                <p>• Live chat available on our website</p>
                <p>• Response time: Usually within 2-4 hours</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Security Notice</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• Your payment information is secure</p>
                <p>• We use industry-standard encryption</p>
                <p>• No card details are stored on our servers</p>
                <p>• All transactions are PCI-compliant</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="text-center mt-12 text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <p>We're sorry for the inconvenience. Our team is here to help you complete your upgrade.</p>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnerUpgradeFailure;
